import type { TFunction } from 'i18next'
import { AuthApiError } from '@/lib/auth/authApi'
import { SocialSignInError } from '@/lib/auth/socialAuth'

/**
 * Better Auth error codes (from the response body's `code`) mapped onto the
 * gateway's translated copy. Anything unknown falls back to the caller's
 * generic per-step message, so server English never reaches the UI.
 */
const CODE_MESSAGES: Record<string, string> = {
  INVALID_EMAIL_OR_PASSWORD: 'errors.wrongPassword',
  EMAIL_NOT_VERIFIED: 'errors.unverified',
  USER_ALREADY_EXISTS: 'errors.accountExists',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'errors.accountExists',
  DISPOSABLE_EMAIL: 'errors.disposableEmail',
  PASSWORD_TOO_SHORT: 'errors.passwordTooShort',
  PASSWORD_TOO_LONG: 'errors.passwordTooLong',
  INVALID_EMAIL: 'errors.invalidEmail',
  INVALID_OTP: 'errors.codeMismatch',
  OTP_EXPIRED: 'errors.codeExpired',
  TOO_MANY_ATTEMPTS: 'errors.codeTooManyAttempts',
}

/** Errors that belong under the code input rather than the password field. */
const CODE_ERRORS = new Set(['INVALID_OTP', 'OTP_EXPIRED', 'TOO_MANY_ATTEMPTS'])

const RATE_LIMITED_STATUS = 429

export function authErrorMessage(err: unknown, t: TFunction<'auth'>, fallbackKey: string): string {
  if (!(err instanceof AuthApiError)) return t(fallbackKey)
  if (err.isNetworkError) return t('errors.network')
  if (err.status === RATE_LIMITED_STATUS) return t('errors.tooMany')
  const key = err.code ? CODE_MESSAGES[err.code] : undefined
  return t(key ?? fallbackKey)
}

/**
 * Google/Apple failures: our popup errors, and the error codes Better Auth puts on the
 * callback (account_not_linked = an account with that email exists but was never verified).
 */
const SOCIAL_MESSAGES: Record<string, string> = {
  popup_blocked: 'errors.socialPopupBlocked',
  timeout: 'errors.socialTimeout',
  account_not_linked: 'errors.socialNotLinked',
  access_denied: 'errors.socialCancelled',
}

export function socialErrorMessage(err: unknown, t: TFunction<'auth'>): string {
  if (err instanceof SocialSignInError) return t(SOCIAL_MESSAGES[err.code] ?? 'errors.socialFailed')
  return authErrorMessage(err, t, 'errors.socialFailed')
}

/** The account exists but its email isn't verified yet (sign-in returns 403). */
export function isUnverifiedError(err: unknown): boolean {
  return err instanceof AuthApiError && err.code === 'EMAIL_NOT_VERIFIED'
}

/** The emailed code was wrong, expired, or used up its attempts. */
export function isCodeError(err: unknown): boolean {
  return err instanceof AuthApiError && err.code !== null && CODE_ERRORS.has(err.code)
}
